import { PageHeader } from "@/components/PageHeader";
import { SortableHeader } from "@/components/SortableHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { AdminServerLogEvent } from "@a7y/types";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  Search,
  X,
} from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type SortField = "id" | "user_id" | "publish_time" | "event_type" | "message" | "country_ip";
type EventTypeFilter = "all" | "error" | "warning";
type OriginHostFilter =
  | "all"
  | "www-staging.apptonomy.ai"
  | "www-sandbox.apptonomy.ai"
  | "www.apptonomy.ai";

const LOOKBACK_OPTIONS: { value: number; label: string }[] = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 180, label: "3 hours" },
  { value: 360, label: "6 hours" },
  { value: 720, label: "12 hours" },
  { value: 1440, label: "24 hours" },
  { value: 4320, label: "3 days" },
  { value: 10080, label: "7 days" },
];

const EVENT_TYPE_OPTIONS: { value: EventTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "error", label: "Error" },
  { value: "warning", label: "Warning" },
];

const ORIGIN_HOST_OPTIONS: { value: OriginHostFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "www-staging.apptonomy.ai", label: "Staging" },
  { value: "www-sandbox.apptonomy.ai", label: "Sandbox" },
  { value: "www.apptonomy.ai", label: "Production" },
];

const EVENT_TYPE_BADGE_COLOR: Record<string, string> = {
  error: "red",
  warning: "amber",
};

export function ErrorsWarningsPage() {
  const navigate = useNavigate();
  const [lookbackMinutes, setLookbackMinutes] = useState(60);
  const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>("all");
  const [originHostFilter, setOriginHostFilter] = useState<OriginHostFilter>("all");
  const [countryIp, setCountryIp] = useState("");
  const [debouncedCountryIp, setDebouncedCountryIp] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("publish_time");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [lookbackOpen, setLookbackOpen] = useState(false);
  const [eventTypeOpen, setEventTypeOpen] = useState(false);
  const [originHostOpen, setOriginHostOpen] = useState(false);

  const pageSize = 50;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCountryIpChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountryIp(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedCountryIp(value);
      setPage(1);
    }, 400);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "publish_time" ? "desc" : "asc");
    }
    setPage(1);
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "errors-warnings",
      {
        lookbackMinutes,
        page,
        sortField,
        sortOrder,
        eventTypeFilter,
        countryIp: debouncedCountryIp,
        originHostFilter,
      },
    ],
    queryFn: () =>
      api.getErrorsWarnings({
        lookbackMinutes,
        page,
        limit: pageSize,
        sortBy: sortField,
        sortOrder,
        event_type: eventTypeFilter === "all" ? undefined : eventTypeFilter,
        country_ip: debouncedCountryIp || undefined,
        origin_host: originHostFilter === "all" ? undefined : originHostFilter,
      }),
  });

  const entries = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const selectedLookbackLabel = LOOKBACK_OPTIONS.find((o) => o.value === lookbackMinutes)?.label ?? "1 hour";
  const selectedEventTypeLabel = EVENT_TYPE_OPTIONS.find((o) => o.value === eventTypeFilter)?.label ?? "All";
  const selectedOriginHostLabel = ORIGIN_HOST_OPTIONS.find((o) => o.value === originHostFilter)?.label ?? "All";

  return (
    <div>
      <PageHeader
        title="Errors & Warnings"
        subtitle="Server-side errors and warnings from application logs"
      />

      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Lookback selector */}
        <div className="relative">
          <button
            onClick={() => setLookbackOpen(!lookbackOpen)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            Lookback: {selectedLookbackLabel}
            <ChevronDown className="h-4 w-4" />
          </button>
          {lookbackOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setLookbackOpen(false)} />
              <div className="absolute left-0 top-full z-20 mt-1 w-44 rounded-lg border border-gray-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-1 shadow-lg">
                {LOOKBACK_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setLookbackMinutes(option.value);
                      setPage(1);
                      setLookbackOpen(false);
                    }}
                    className={`flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-zinc-700 ${
                      lookbackMinutes === option.value
                        ? "text-blue-600 dark:text-blue-400 font-medium"
                        : "text-gray-700 dark:text-zinc-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Event type filter */}
        <div className="relative">
          <button
            onClick={() => setEventTypeOpen(!eventTypeOpen)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            Type: {selectedEventTypeLabel}
            <ChevronDown className="h-4 w-4" />
          </button>
          {eventTypeOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setEventTypeOpen(false)} />
              <div className="absolute left-0 top-full z-20 mt-1 w-44 rounded-lg border border-gray-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-1 shadow-lg">
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setEventTypeFilter(option.value);
                      setPage(1);
                      setEventTypeOpen(false);
                    }}
                    className={`flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-zinc-700 ${
                      eventTypeFilter === option.value
                        ? "text-blue-600 dark:text-blue-400 font-medium"
                        : "text-gray-700 dark:text-zinc-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Origin host filter */}
        <div className="relative">
          <button
            onClick={() => setOriginHostOpen(!originHostOpen)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            Host: {selectedOriginHostLabel}
            <ChevronDown className="h-4 w-4" />
          </button>
          {originHostOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOriginHostOpen(false)} />
              <div className="absolute left-0 top-full z-20 mt-1 w-44 rounded-lg border border-gray-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-1 shadow-lg">
                {ORIGIN_HOST_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setOriginHostFilter(option.value);
                      setPage(1);
                      setOriginHostOpen(false);
                    }}
                    className={`flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-zinc-700 ${
                      originHostFilter === option.value
                        ? "text-blue-600 dark:text-blue-400 font-medium"
                        : "text-gray-700 dark:text-zinc-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Country IP text input */}
        <div className="relative w-[200px]">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Filter by country..."
            value={countryIp}
            onChange={handleCountryIpChange}
            className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-10 pr-10 py-2 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {countryIp && (
            <button
              onClick={() => {
                setCountryIp("");
                setDebouncedCountryIp("");
                setPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <SortableHeader field="publish_time" label="Time" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="event_type" label="Type" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="message" label="Message" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="id" label="Session ID" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="user_id" label="User ID" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="country_ip" label="Country" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600 mx-auto" />
              </TableCell>
            </TableRow>
          ) : entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <AlertTriangle className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 font-medium text-zinc-500">No errors or warnings found</p>
                <p className="text-sm text-zinc-400">
                  Try adjusting the lookback window or filters
                </p>
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry: AdminServerLogEvent) => (
              <TableRow
                key={entry.message_id ?? `${entry.publish_time}-${entry.message}`}
                className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
                onClick={() => {
                  if (entry.message_id) {
                    navigate(`/admin/errors-warnings/${entry.message_id}`);
                  }
                }}
              >
                <TableCell className="whitespace-nowrap text-zinc-500" title={entry.publish_time ?? undefined}>
                  {entry.publish_time ? formatDate(entry.publish_time) : "\u2014"}
                </TableCell>
                <TableCell>
                  <Badge color={EVENT_TYPE_BADGE_COLOR[entry.event_type] ?? "zinc"}>
                    {entry.event_type}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-[300px] truncate text-zinc-500">
                  {entry.message}
                </TableCell>
                <TableCell className="max-w-[120px] truncate font-mono text-xs text-zinc-500">
                  {entry.id ?? "\u2014"}
                </TableCell>
                <TableCell className="max-w-[120px] truncate font-mono text-xs text-zinc-500">
                  {entry.user_id ?? "\u2014"}
                </TableCell>
                <TableCell className="text-zinc-500">
                  {entry.country_ip ?? "\u2014"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="mt-6 flex items-center justify-between">
          <Text className="text-sm text-gray-700 dark:text-zinc-300">
            Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{" "}
            <span className="font-medium">{Math.min(page * pageSize, totalCount)}</span> of{" "}
            <span className="font-medium">{totalCount}</span> results
          </Text>
          <div className="flex gap-2">
            <Button onClick={() => setPage(page - 1)} disabled={page === 1} outline>
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * pageSize >= totalCount}
              outline
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
